/**
 * @file Marketing landing page (new-ui port).
 *
 * Verbatim port of the new-ui `LandingPage` (same markup + lp-* classNames,
 * styled by ./landing.css which is copied verbatim from new-ui's Landing.css).
 * Differences from new-ui are limited to platform integration:
 *   - Instagram is listed with the other platforms (new-ui's landing page omits
 *     it); its logo and colour are new-ui's own, from SocialMediaManager/ShareModal
 *   - strings go through i18next.t("<English sentence>") (app-home key convention)
 *   - theme + language come from props (router.tsx owns that state)
 *   - CTAs use app-home's outbound conventions (/app/signup?plan=…, redirectGetStarted)
 *     and carry data-track-id so the existing action tracker labels them.
 */
import React, { useEffect, useState, useRef } from 'react';
import i18next from 'i18next';
import {
  ArrowRight, Bookmark, Users, Check, Star, Sparkles,
  CheckCircle2, Share2, Search, Sun, Moon, Monitor, Globe, ChevronDown,
} from 'lucide-react';
// @ts-ignore — react-google-recaptcha ships no bundled types
import ReCAPTCHA from 'react-google-recaptcha';
import { Request } from '../../auth/request';
import './landing-base.css';
import './landing.css';

// Production reCAPTCHA site key — same key the legacy app-home contact form used.
const RECAPTCHA_SITE_KEY = '6LcxQjgpAAAAAKe7EBOCgO0KBirP2umkQqBi1B-i';

// Brand assets are served from the shared GCS bucket (same as app-home's other
// static images) so they resolve in the deployed build, not just local /public.
const LOGO_URL = 'https://storage.googleapis.com/clipsymphony_static_assets/Logo.png';
const HERO_SCREENSHOT_URL = 'https://storage.googleapis.com/clipsymphony_static_assets/hero-screenshot.png';

type ThemeMode = 'light' | 'dark' | 'system';

interface LandingProps {
  /** token-aware redirect helper from router.tsx (redirectGetStarted) */
  signUp: (url: string) => void;
  language: string;
  onChangeLanguage: (lang: string) => void;
  history?: any;
}

/* ── Platform SVG logos ───────────────────────────────── */
function BlueSkyIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 600 530" fill="currentColor" aria-hidden="true">
      <path d="M135.72 44.03C202.216 93.951 273.74 195.17 300 249.49c26.262-54.316 97.782-155.54 164.28-205.46C512.26 8.009 590-19.862 590 68.825c0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.38-3.69-10.832-3.708-7.896-.017-2.936-1.193.516-3.707 7.896-13.714 40.255-67.233 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.108 11.421-142.55-7.449-163.25-81.433C20.15 217.613 10 86.532 10 68.82 10-19.867 87.74 8.009 135.72 44.03z"/>
    </svg>
  );
}
function MastodonIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 61 65" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <path d="M60.7539 14.3904C59.8143 7.40642 53.7273 1.90257 46.5117 0.836066C45.2943 0.655854 40.6819 0 29.9973 0H29.9175C19.2299 0 16.937 0.655854 15.7196 0.836066C8.70488 1.87302 2.29885 6.81852 0.744617 13.8852C-0.00294988 17.3654 -0.0827298 21.2237 0.0561464 24.7629C0.254119 29.8384 0.292531 34.905 0.753482 39.9598C1.07215 43.3175 1.62806 46.6484 2.41704 49.9276C3.89445 55.9839 9.87499 61.0239 15.7344 63.0801C22.0077 65.2244 28.7542 65.5804 35.2184 64.1082C35.9295 63.9428 36.6318 63.7508 37.3252 63.5321C38.8971 63.0329 40.738 62.4745 42.0913 61.4937C42.1099 61.4799 42.1251 61.4621 42.1358 61.4417C42.1466 61.4212 42.1526 61.3986 42.1534 61.3755V56.4773C42.153 56.4557 42.1479 56.4345 42.1383 56.4151C42.1287 56.3958 42.1149 56.3788 42.0979 56.3655C42.0809 56.3522 42.0611 56.3429 42.04 56.3382C42.019 56.3335 41.9971 56.3336 41.9761 56.3384C37.8345 57.3276 33.5905 57.8234 29.3324 57.8156C22.0045 57.8156 20.0336 54.3384 19.4693 52.8908C19.0156 51.6397 18.7275 50.3346 18.6124 49.0088C18.6112 48.9866 18.6153 48.9643 18.6243 48.9439C18.6333 48.9236 18.647 48.9056 18.6643 48.8915C18.6816 48.8774 18.7019 48.8675 18.7237 48.8628C18.7455 48.858 18.7681 48.8585 18.7897 48.8641C22.8622 49.8465 27.037 50.3423 31.2265 50.3412C32.234 50.3412 33.2387 50.3412 34.2463 50.3146C38.4598 50.1964 42.9009 49.9808 47.0465 49.1713C47.1499 49.1506 47.2534 49.1329 47.342 49.1063C53.881 47.8507 60.1038 43.9097 60.7362 33.9301C60.7598 33.5372 60.8189 29.8148 60.8189 29.4071C60.8218 28.0215 61.2651 19.5781 60.7539 14.3904Z" fill="url(#mastodon_grad)"/>
      <path d="M50.3943 22.237V39.5876H43.5185V22.7481C43.5185 19.2029 42.0411 17.3949 39.036 17.3949C35.7325 17.3949 34.0778 19.5338 34.0778 23.7585V32.9759H27.2434V23.7585C27.2434 19.5338 25.5857 17.3949 22.2822 17.3949C19.2949 17.3949 17.8027 19.2029 17.8027 22.7481V39.5876H10.9298V22.237C10.9298 18.6918 11.835 15.8754 13.6453 13.7877C15.5128 11.7049 17.9623 10.6355 21.0028 10.6355C24.522 10.6355 27.1813 11.9885 28.9542 14.6917L30.665 17.5633L32.3788 14.6917C34.1517 11.9885 36.811 10.6355 40.3243 10.6355C43.3619 10.6355 45.8114 11.7049 47.6847 13.7877C49.4931 15.8734 50.3963 18.6899 50.3943 22.237Z" fill="white"/>
      <defs>
        <linearGradient id="mastodon_grad" x1="30.5" y1="0" x2="30.5" y2="65" gradientUnits="userSpaceOnUse">
          <stop stopColor="#6364FF"/>
          <stop offset="1" stopColor="#563ACC"/>
        </linearGradient>
      </defs>
    </svg>
  );
}
function FacebookLogoIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  );
}
function InstagramLogoIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
  );
}
function XLogoIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.258 5.622 5.906-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
}
function ThreadsLogoIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z"/>
    </svg>
  );
}
function LinkedInLogoIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  );
}
function YouTubeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  );
}

/* ── Footer social logos (larger) ────────────────────── */
function FacebookLogo({ size = 20 }: { size?: number }) { return <FacebookLogoIcon size={size} />; }
function XLogo({ size = 20 }: { size?: number }) { return <XLogoIcon size={size} />; }
function LinkedInLogo({ size = 20 }: { size?: number }) { return <LinkedInLogoIcon size={size} />; }

/* ── Platform definitions ────────────────────────────── */
const PLATFORMS = [
  { name: 'Bluesky',     color: '#0085ff', bg: '#eff6ff', Icon: BlueSkyIcon      },
  { name: 'Mastodon',    color: '#6364ff', bg: '#eef2ff', Icon: MastodonIcon     },
  { name: 'Facebook',    color: '#1877f2', bg: '#eff6ff', Icon: FacebookLogoIcon },
  { name: 'Instagram',   color: '#E4405F', bg: '#fdf2f8', Icon: InstagramLogoIcon },
  { name: 'X',           color: '#0f172a', bg: '#f1f5f9', Icon: XLogoIcon        },
  { name: 'Threads',     color: '#000000', bg: '#f1f5f9', Icon: ThreadsLogoIcon  },
  { name: 'LinkedIn',    color: '#0a66c2', bg: '#eff6ff', Icon: LinkedInLogoIcon },
  { name: 'YouTube',     color: '#ff0000', bg: '#fef2f2', Icon: YouTubeIcon      },
];
const PLATFORM_COUNT = PLATFORMS.length;

/* ── Nav ─────────────────────────────────────────────── */
const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
];

interface NavProps {
  scrolled: boolean;
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  language: string;
  setLanguage: (lang: string) => void;
  signUp: (url: string) => void;
}

function Nav({ scrolled, theme, setTheme, language, setLanguage, signUp }: NavProps) {
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => { if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  const nextTheme: ThemeMode = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
  const ThemeIcon = theme === 'dark' ? Moon : theme === 'system' ? Monitor : Sun;
  const currentLang = LANGUAGE_OPTIONS.find(l => l.code === language) || LANGUAGE_OPTIONS[0];

  return (
    <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`}>
      <a href="/" className="lp-nav-brand">
        <img src={LOGO_URL} alt="ClipSymphony" className="lp-nav-brand-logo-full" />
      </a>
      <div className="lp-nav-links">
        <a href="#features"   className="lp-nav-link hide-mobile">{i18next.t("Features")}</a>
        <a href="#platforms"  className="lp-nav-link hide-mobile">{i18next.t("Platforms")}</a>
        <a href="#pricing"    className="lp-nav-link hide-mobile">{i18next.t("Pricing")}</a>
        <a href="#contact"    className="lp-nav-link hide-mobile">{i18next.t("Contact")}</a>

        {/* Language switcher */}
        <div className="lp-lang-switcher" ref={langRef}>
          <button
            className="lp-nav-icon-btn"
            onClick={() => setLangOpen(o => !o)}
            aria-label={i18next.t("Select language")}
            title={i18next.t("Language")}
            data-track-id="landing-nav-language"
          >
            <Globe size={15} />
            <span className="lp-lang-label hide-mobile">{currentLang.code.toUpperCase()}</span>
            <ChevronDown size={12} />
          </button>
          {langOpen && (
            <div className="lp-lang-dropdown">
              {LANGUAGE_OPTIONS.map(opt => (
                <button
                  key={opt.code}
                  className={`lp-lang-option${language === opt.code ? ' active' : ''}`}
                  onClick={() => { setLanguage(opt.code); setLangOpen(false); }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Theme switcher */}
        <button
          className="lp-nav-icon-btn"
          onClick={() => setTheme(nextTheme)}
          aria-label={i18next.t("Switch theme")}
          title={i18next.t("Theme")}
          data-track-id="landing-nav-theme"
        >
          <ThemeIcon size={15} />
        </button>

        <a className="lp-nav-link" onClick={() => signUp("app/login")} data-track-id="landing-nav-login" style={{ cursor: 'pointer' }}>{i18next.t("Login")}</a>
        <a className="lp-cta" onClick={() => signUp("app/signup")} data-track-id="landing-nav-getstarted" style={{ cursor: 'pointer' }}>{i18next.t("Get Started")} <ArrowRight size={14} /></a>
      </div>
    </nav>
  );
}

/* ── Contact form ────────────────────────────────────── */
function ContactSection() {
  const [form, setForm]     = useState({ name: '', email: '', subject: '', message: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sent, setSent]     = useState(false);
  // reCAPTCHA + server submission — preserved from the legacy app-home contact form.
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [postError, setPostError] = useState('');
  const recaptchaRef = useRef<any>(null);

  const set = (k: string) => (e: any) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim())                   errs.name    = i18next.t("Name is required.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email   = i18next.t("Enter a valid email.");
    if (!form.subject.trim())                errs.subject = i18next.t("Subject is required.");
    if (form.message.trim().length < 10)     errs.message = i18next.t("Message is too short.");
    if (!recaptchaToken)                     errs.recaptcha = i18next.t("Please complete captcha challenge");
    return errs;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    setPostError('');
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    Request
      .post("/api/contact-form", {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        recaptchaToken,
      })
      .then((r: any) => {
        if (["200", "201"].includes(String(r && r.status))) {
          setSent(true);
        } else {
          setPostError(r && r.message ? r.message : "Error making request Please try again");
          recaptchaRef.current?.reset?.();
          setRecaptchaToken('');
        }
      })
      .catch(() => {
        setPostError("Error making request Please try again");
        recaptchaRef.current?.reset?.();
        setRecaptchaToken('');
      });
  };

  return (
    <section id="contact" className="lp-section" style={{ paddingTop: 0 }}>
      <div className="lp-section-head">
        <span className="lp-tag">{i18next.t("Contact")}</span>
        <h2 className="lp-section-title">{i18next.t("Get in touch")}</h2>
        <p className="lp-section-sub">{i18next.t("Have a question? We'd love to hear from you.")}</p>
      </div>

      <div className="lp-contact-wrap">
        {sent ? (
          <div className="lp-contact-success">
            <div className="lp-contact-success-icon"><CheckCircle2 size={26} /></div>
            <h3>{i18next.t("Message sent!")}</h3>
            <p>{i18next.t("Thanks! We'll get back to you within one business day.")}</p>
            <button
              type="button"
              className="lp-secondary"
              style={{ marginTop: '0.5rem' }}
              onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
            >
              {i18next.t("Send another message")}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="lp-contact-grid">
              <div className="lp-contact-field">
                <label htmlFor="c-name">{i18next.t("Name")} <span className="req">*</span></label>
                <input id="c-name" className="lp-contact-input" type="text" placeholder={i18next.t("Jane Doe")}
                  value={form.name} onChange={set('name')} autoComplete="name" />
                {errors.name && <span className="lp-contact-error">{errors.name}</span>}
              </div>

              <div className="lp-contact-field">
                <label htmlFor="c-email">{i18next.t("Email")} <span className="req">*</span></label>
                <input id="c-email" className="lp-contact-input" type="email" placeholder={i18next.t("you@example.com")}
                  value={form.email} onChange={set('email')} autoComplete="email" />
                {errors.email && <span className="lp-contact-error">{errors.email}</span>}
              </div>

              <div className="lp-contact-field lp-contact-span-full">
                <label htmlFor="c-subject">{i18next.t("Subject")} <span className="req">*</span></label>
                <input id="c-subject" className="lp-contact-input" type="text" placeholder={i18next.t("How can we help?")}
                  value={form.subject} onChange={set('subject')} />
                {errors.subject && <span className="lp-contact-error">{errors.subject}</span>}
              </div>

              <div className="lp-contact-field lp-contact-span-full">
                <label htmlFor="c-message">{i18next.t("Message")} <span className="req">*</span></label>
                <textarea id="c-message" className="lp-contact-textarea" placeholder={i18next.t("Tell us more…")}
                  value={form.message} onChange={set('message')} />
                {errors.message && <span className="lp-contact-error">{errors.message}</span>}
              </div>

              <div className="lp-contact-field lp-contact-span-full">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token: any) => { setRecaptchaToken(token || ''); setErrors((p) => ({ ...p, recaptcha: '' })); }}
                  onExpired={() => setRecaptchaToken('')}
                  onErrored={() => setRecaptchaToken('')}
                />
                {errors.recaptcha && <span className="lp-contact-error">{errors.recaptcha}</span>}
              </div>

              <div className="lp-contact-span-full">
                <button type="submit" className="lp-cta lp-cta-lg" data-track-id="landing-contact-submit">
                  {i18next.t("Send message")} <ArrowRight size={16} />
                </button>
                {postError && <div className="lp-contact-error" style={{ marginTop: '0.6rem' }}>{i18next.t(postError)}</div>}
              </div>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

/* ── Main page ───────────────────────────────────────── */
// The public site is open to everyone, signed in or not. It used to send a
// signed-in visitor straight into the app, so they could never reach the home
// page or any marketing page; only /app/login forwards a live session now.
export default function Landing({ signUp, language, onChangeLanguage }: LandingProps) {
  const [scrolled, setScrolled] = useState(false);

  // Self-managed theme — verbatim from new-ui's SettingsContext. Drives `data-theme`
  // on <html>, which the landing CSS variables key off. This is independent of
  // app-home's own (.dark) dark mode, which is left untouched.
  const [theme, setThemeState] = useState<ThemeMode>('system');
  const setTheme = (t: ThemeMode) => {
    setThemeState(t);
    const root = document.documentElement;
    if (t === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', t);
    }
  };

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <div className="landing">
      <Nav
        scrolled={scrolled}
        theme={theme}
        setTheme={setTheme}
        language={language}
        setLanguage={onChangeLanguage}
        signUp={signUp}
      />

      {/* ════════════════════════════════════════
          HERO
      ════════════════════════════════════════ */}
      <section className="lp-hero">
        <div className="lp-hero-grid">

          {/* Left: copy */}
          <div>
            <div className="lp-eyebrow">
              <span className="pill">{i18next.t("AI-powered")}</span>
              {i18next.t("The platform for teams who publish")}
            </div>

            <h1 className="lp-headline">
              {i18next.t("Save. Schedule.")}<br />
              <span className="grad">{i18next.t("Publish everywhere.")}</span>
            </h1>

            <p className="lp-sub">
              {i18next.t("Your complete content workflow in one place — save and organise articles, write with AI, collaborate with your team, then publish to Bluesky, Mastodon, Facebook, Instagram, X, Threads, LinkedIn, and YouTube in a single click.")}
            </p>

            {/* Platform pills */}
            <div className="lp-platform-pills">
              {PLATFORMS.map((p) => (
                <span
                  key={p.name}
                  className="lp-platform-pill"
                  style={{ background: p.bg, color: p.color, border: `1px solid ${p.color}22` }}
                >
                  <span className="pill-logo" style={{ color: p.color }}><p.Icon size={12} /></span>
                  {p.name}
                </span>
              ))}
            </div>

            <div className="lp-hero-actions">
              <a className="lp-cta lp-cta-lg" onClick={() => signUp("app/signup")} data-track-id="landing-hero-signup" style={{ cursor: 'pointer' }}>
                {i18next.t("Start free — no credit card")} <ArrowRight size={16} />
              </a>
              <a href="#features" className="lp-secondary">{i18next.t("See how it works")}</a>
            </div>

            <div className="lp-trust">
              <span className="checks"><Check size={13} /> {i18next.t("Free for individuals")}</span>
              <span className="sep" />
              <span className="checks"><Check size={13} /> {i18next.t("30-day team trial")}</span>
              <span className="sep" />
              <span className="checks"><Check size={13} /> {i18next.t("Cancel anytime")}</span>
            </div>
          </div>

          {/* Right: product visual */}
          <div className="lp-hero-art" aria-hidden="true">
            <div className="lp-floating-chip lp-chip-1">
              <span className="ico"><Sparkles size={12} /></span>
              {i18next.t("AI writing your captions")}
            </div>
            <div className="lp-floating-chip lp-chip-2">
              <span className="ico"><Check size={12} /></span>
              {i18next.t("Published to {{count}} platforms", { count: PLATFORM_COUNT })}
            </div>

            <div className="lp-hero-card">
              <div className="lp-hero-card-bar">
                <span className="dot r" /><span className="dot y" /><span className="dot g" />
                <span className="url">clipsymphony.com/workspace</span>
              </div>
              <img
                src={HERO_SCREENSHOT_URL}
                alt="ClipSymphony social media publisher"
                className="lp-hero-screenshot"
              />
            </div>
          </div>

        </div>
      </section>


      {/* ════════════════════════════════════════
          FEATURE PILLARS
      ════════════════════════════════════════ */}
      <section id="features" className="lp-section">
        <div className="lp-section-head">
          <span className="lp-tag">{i18next.t("Features")}</span>
          <h2 className="lp-section-title">{i18next.t("Four tools. One platform.")}</h2>
          <p className="lp-section-sub">
            {i18next.t("Everything your content team needs — tightly integrated so nothing falls through the cracks.")}
          </p>
        </div>

        <div className="lp-pillars">

          {/* Pillar 1 — Save & Organise */}
          <div className="lp-pillar">
            <div className="lp-pillar-visual">
              <div className="pv-item">
                <span className="pv-tag tag-blue">{i18next.t("Research")}</span>
                <span className="pv-title">{i18next.t("The Future of Open Social Networks")}</span>
              </div>
              <div className="pv-item">
                <span className="pv-tag tag-purple">{i18next.t("Draft")}</span>
                <span className="pv-title">{i18next.t("Q3 Product Update — Campaign Brief")}</span>
              </div>
              <div className="pv-item">
                <span className="pv-tag tag-green">{i18next.t("Published")}</span>
                <span className="pv-title">{i18next.t("Content strategy: What media teams need to know")}</span>
              </div>
              <div className="pv-item">
                <span className="pv-tag tag-amber">{i18next.t("Scheduled")}</span>
                <span className="pv-title">{i18next.t("Social media trends — weekly digest")}</span>
              </div>
              <div className="pv-search">
                <Search size={10} /> {i18next.t("Search 847 saved articles…")}
              </div>
            </div>
            <div className="lp-pillar-body">
              <span className="lp-pillar-icon"><Bookmark size={18} /></span>
              <h3>{i18next.t("Save & Organise")}</h3>
              <p>{i18next.t("Build a searchable content library. Clip articles, tag them, and find anything instantly — across every workspace.")}</p>
              <ul>
                <li><Check size={13} /> {i18next.t("Smart tags and full-text search")}</li>
                <li><Check size={13} /> {i18next.t("Collections, workspaces and folders")}</li>
              </ul>
            </div>
          </div>

          {/* Pillar 2 — Publish Everywhere */}
          <div className="lp-pillar">
            <div className="lp-pillar-visual">
              <div className="pv-post-thumb">
                <div className="pv-post-line w80" />
                <div className="pv-post-line w60" />
                <div className="pv-post-line w72" />
              </div>
              <div className="pv-arrow">{i18next.t("↓ One click · {{count}} platforms", { count: PLATFORM_COUNT })}</div>
              <div className="pv-platform-wrap">
                {PLATFORMS.map((p) => (
                  <span key={p.name} className="pv-plt-badge"
                    style={{ background: p.bg, color: p.color, borderColor: `${p.color}33` }}>
                    <p.Icon size={11} />
                    {p.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="lp-pillar-body">
              <span className="lp-pillar-icon accent-icon"><Share2 size={18} /></span>
              <h3>{i18next.t("Publish Everywhere")}</h3>
              <p>{i18next.t("Write once and distribute across Bluesky, Mastodon, Facebook, Instagram, X, Threads, LinkedIn, and YouTube — simultaneously, no copy-pasting.")}</p>
              <ul>
                <li><Check size={13} /> {i18next.t("{{count}} platforms in a single click", { count: PLATFORM_COUNT })}</li>
                <li><Check size={13} /> {i18next.t("Per-platform formatting & preview")}</li>
                <li><Check size={13} /> {i18next.t("Smart scheduling queue")}</li>
              </ul>
            </div>
          </div>

          {/* Pillar 3 — AI Writing Assistant */}
          <div className="lp-pillar">
            <div className="lp-pillar-visual">
              <div className="pv-ai-prompt">
                <Sparkles size={10} />
                <span>{i18next.t("Write a LinkedIn post about this article")}</span>
              </div>
              <div className="pv-ai-response">
                {i18next.t("\"Excited to share our latest deep-dive into ActivityPub adoption — the open social web is closer than most people think. Here's what media teams need to know…\"")}
              </div>
              <div className="pv-ai-actions">
                <span>{i18next.t("Shorter")}</span>
                <span>{i18next.t("Add emoji")}</span>
                <span>{i18next.t("More formal")}</span>
                <span>{i18next.t("Add CTA")}</span>
              </div>
            </div>
            <div className="lp-pillar-body">
              <span className="lp-pillar-icon"><Sparkles size={18} /></span>
              <h3>{i18next.t("AI Writing Assistant")}</h3>
              <p>{i18next.t("Draft captions, generate summaries, and get platform-specific suggestions — powered by AI, always guided by you.")}</p>
              <ul>
                <li><Check size={13} /> {i18next.t("Platform-optimised copy for each channel")}</li>
                <li><Check size={13} /> {i18next.t("Hashtag & timing recommendations")}</li>
                <li><Check size={13} /> {i18next.t("Auto-summarise long-form content")}</li>
              </ul>
            </div>
          </div>

          {/* Pillar 4 — Team Workflows */}
          <div className="lp-pillar">
            <div className="lp-pillar-visual">
              <div className="pv-workflow">
                <div className="pv-stage done">{i18next.t("Draft")}</div>
                <div className="pv-stage-arrow">→</div>
                <div className="pv-stage active">{i18next.t("Review")}</div>
                <div className="pv-stage-arrow">→</div>
                <div className="pv-stage">{i18next.t("Approved")}</div>
                <div className="pv-stage-arrow">→</div>
                <div className="pv-stage">{i18next.t("Live")}</div>
              </div>
              <div className="pv-comments">
                <div className="pv-comment">
                  <span className="pv-avatar">SR</span>
                  <span>{i18next.t("\"Looks great — maybe add a stronger CTA?\"")}</span>
                </div>
                <div className="pv-comment">
                  <span className="pv-avatar" style={{ background: 'linear-gradient(135deg,#059669,#047857)' }}>MR</span>
                  <span>{i18next.t("Done! Approving now ✓")}</span>
                </div>
                <div className="pv-comment">
                  <span className="pv-avatar" style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)' }}>JK</span>
                  <span>{i18next.t("Scheduled for tomorrow 9 am 🚀")}</span>
                </div>
              </div>
            </div>
            <div className="lp-pillar-body">
              <span className="lp-pillar-icon"><Users size={18} /></span>
              <h3>{i18next.t("Team Workflows")}</h3>
              <p>{i18next.t("Collaborate in real-time, run approval flows, and publish confidently — without the chaos of email threads and shared docs.")}</p>
              <ul>
                <li><Check size={13} /> {i18next.t("Role-based access & permissions")}</li>
                <li><Check size={13} /> {i18next.t("Draft → Review → Approve → Publish")}</li>
                <li><Check size={13} /> {i18next.t("Shared workspaces and activity feed")}</li>
              </ul>
            </div>
          </div>

        </div>
      </section>

      {/* ════════════════════════════════════════
          PLATFORM GRID
      ════════════════════════════════════════ */}
      <section id="platforms" className="lp-platform-section">
        <div className="lp-section-head" style={{ marginBottom: '2.25rem' }}>
          <span className="lp-tag">{i18next.t("Integrations")}</span>
          <h2 className="lp-section-title">
            {i18next.t("AT Protocol. Open web.")}<br />
            <span className="grad">{i18next.t("And everywhere in between.")}</span>
          </h2>
          <p className="lp-section-sub">
            {i18next.t("First-class support for the open, decentralised web — alongside every major commercial network.")}
          </p>
        </div>

        <div className="lp-platform-grid">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="lp-platform-tile">
              <span className="lp-plt-icon" style={{ background: p.bg, color: p.color }}>
                <p.Icon size={20} />
              </span>
              <span className="lp-plt-name">{p.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
          HOW IT WORKS
      ════════════════════════════════════════ */}
      <section id="how" className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-section-head">
          <span className="lp-tag">{i18next.t("How it works")}</span>
          <h2 className="lp-section-title">{i18next.t("From idea to everywhere in three steps.")}</h2>
        </div>

        <div className="lp-steps">
          {[
            {
              n: 1,
              icon: <Bookmark size={18} />,
              title: i18next.t("Save & organise"),
              body: i18next.t("Capture articles, bookmark ideas, and build your team's shared content library — all in one searchable place."),
            },
            {
              n: 2,
              icon: <Sparkles size={18} />,
              title: i18next.t("Write & collaborate"),
              body: i18next.t("Use AI to draft, your team to review, and approval flows to stay on-brand before anything goes live."),
            },
            {
              n: 3,
              icon: <Share2 size={18} />,
              title: i18next.t("Schedule & publish"),
              body: i18next.t("Set the time, hit Publish, and ClipSymphony distributes to every connected platform instantly — or queues it for peak engagement."),
            },
          ].map(({ n, icon, title, body }) => (
            <div key={n} className="lp-step">
              <div className="lp-step-icon">{icon}</div>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
          TESTIMONIALS
      ════════════════════════════════════════ */}
      <section className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-testimonials">
          <div className="lp-quote-wrap">
            <div className="lp-quote-mark">"</div>
            <p className="lp-quote">
              {i18next.t("We used to spend two hours manually cross-posting every article. With ClipSymphony it takes ten seconds — and our reach doubled because we're actually consistent now.")}
            </p>
            <div className="lp-quote-author">
              <span className="avatar">MR</span>
              <span><strong>Maya Rivera</strong> · {i18next.t("Head of Content, Lumen")}</span>
              <span style={{ color: '#f59e0b', display: 'inline-flex', gap: 2 }}>
                {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#f59e0b" />)}
              </span>
            </div>
          </div>

          <div className="lp-quote-wrap">
            <div className="lp-quote-mark">"</div>
            <p className="lp-quote">
              {i18next.t("The AI assistant alone saves our team four hours a week. The ActivityPub and Bluesky integrations are the best we've seen — native, not bolted on.")}
            </p>
            <div className="lp-quote-author">
              <span className="avatar" style={{ background: 'linear-gradient(135deg,#059669,#047857)' }}>JK</span>
              <span><strong>James Kaur</strong> · {i18next.t("Editor-in-Chief, Northwind Media")}</span>
              <span style={{ color: '#f59e0b', display: 'inline-flex', gap: 2 }}>
                {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="#f59e0b" />)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          PRICING
      ════════════════════════════════════════ */}
      <section id="pricing" className="lp-section" style={{ paddingTop: 0 }}>
        <div className="lp-section-head">
          <span className="lp-tag">{i18next.t("Pricing")}</span>
          <h2 className="lp-section-title">{i18next.t("Simple, transparent pricing.")}</h2>
          <p className="lp-section-sub">{i18next.t("Start free. Scale when your team is ready. No hidden fees.")}</p>
        </div>

        <div className="lp-pricing">
          {/* Individual */}
          <div className="lp-plan">
            <h3>{i18next.t("Individual")}</h3>
            <div className="lp-plan-price">€0<small>{i18next.t("/forever")}</small></div>
            <p className="lp-plan-sub">{i18next.t("Everything you need as a solo creator.")}</p>
            <ul>
              <li><Check size={15} /> {i18next.t("Personal workspace")}</li>
              <li><Check size={15} /> {i18next.t("2 GB storage included")}</li>
              <li><Check size={15} /> {i18next.t("AI writing assistant")}</li>
            </ul>
            <a href="/app/signup?plan=individual" className="lp-secondary"
              style={{ justifyContent: 'center', marginTop: 'auto' }} data-track-id="landing-pricing-individual">
              {i18next.t("Get started free")}
            </a>
          </div>

          {/* Professional */}
          <div className="lp-plan featured">
            <span className="lp-plan-pop">{i18next.t("Most popular")}</span>
            <h3>{i18next.t("Professional")}</h3>
            <div className="lp-plan-price">€89<small>{i18next.t("/user/mo")}</small></div>
            <p className="lp-plan-sub">{i18next.t("For teams that publish at scale.")}</p>
            <ul>
              <li><Check size={15} /> {i18next.t("Everything in Individual")}</li>
              <li><Check size={15} /> {i18next.t("Unlimited channels & workspaces")}</li>
              <li><Check size={15} /> {i18next.t("Team collaboration & approvals")}</li>
              <li><Check size={15} /> {i18next.t("30-day free trial")}</li>
            </ul>
            <a href="/app/signup?plan=business" className="lp-cta"
              style={{ justifyContent: 'center', marginTop: 'auto' }} data-track-id="landing-pricing-business">
              {i18next.t("Start 30-day trial")} <ArrowRight size={14} />
            </a>
          </div>

          {/* Enterprise */}
          <div className="lp-plan enterprise">
            <h3>{i18next.t("Enterprise")}</h3>
            <div className="lp-plan-price">{i18next.t("Custom")}</div>
            <p className="lp-plan-sub">{i18next.t("For large organisations with advanced needs.")}</p>
            <ul>
              <li><Check size={15} /> {i18next.t("Everything in Professional")}</li>
              <li><Check size={15} /> {i18next.t("20+ users minimum")}</li>
              <li><Check size={15} /> {i18next.t("Dedicated account manager")}</li>
              <li><Check size={15} /> {i18next.t("Custom SLA & onboarding")}</li>
            </ul>
            <a href="/app/contact-sales?plan=enterprise" className="lp-cta enterprise-cta"
              style={{ justifyContent: 'center', marginTop: 'auto' }} data-track-id="landing-pricing-enterprise">
              {i18next.t("Contact Sales")} <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <ContactSection />

      {/* ════════════════════════════════════════
          CTA BANNER
      ════════════════════════════════════════ */}
      <div className="lp-cta-banner">
        <h2>{i18next.t("Stop juggling tools. Start publishing everywhere.")}</h2>
        <p>{i18next.t("Join thousands of media teams who save hours every week with ClipSymphony.")}</p>
        <div style={{ display: 'inline-flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
          <a className="lp-cta lp-cta-lg" onClick={() => signUp("app/signup")} data-track-id="landing-cta-signup" style={{ cursor: 'pointer' }}>
            {i18next.t("Create your account free")} <ArrowRight size={16} />
          </a>
          <a className="lp-secondary" onClick={() => signUp("app/login")} data-track-id="landing-cta-login" style={{ cursor: 'pointer' }}>{i18next.t("I already have an account")}</a>
        </div>
      </div>

      {/* ════════════════════════════════════════
          FOOTER
      ════════════════════════════════════════ */}
      <footer className="lp-footer">
        <div className="lp-footer-new-inner">

          {/* Social icons */}
          <div className="lp-footer-social">
            <a
              href="https://www.facebook.com/ClipSymphony"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-footer-social-link"
              aria-label="ClipSymphony on Facebook"
              title="Facebook"
            >
              <FacebookLogo size={20} />
            </a>
            <a
              href="https://x.com/clipsymphonyInc"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-footer-social-link"
              aria-label="ClipSymphony on X (Twitter)"
              title="X (Twitter)"
            >
              <XLogo size={20} />
            </a>
            <a
              href="https://www.linkedin.com/company/clipsymphony"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-footer-social-link"
              aria-label="ClipSymphony on LinkedIn"
              title="LinkedIn"
            >
              <LinkedInLogo size={20} />
            </a>
          </div>

          {/* Company address */}
          <address className="lp-footer-address">
            SCARATEC IT-Consulting GmbH<br />
            Martin-Luther-Platz 22<br />
            40212 Düsseldorf<br />
            {i18next.t("Germany")}
          </address>

          {/* Legal links */}
          <div className="lp-footer-legal-links">
            <a href="/app/terms-and-conditions">{i18next.t("Terms and Conditions")}</a>
            <span className="lp-footer-sep">·</span>
            <a href="/app/privacy-policy">{i18next.t("Privacy Policy")}</a>
          </div>

          {/* Copyright */}
          <div className="lp-footer-copyright">
            {i18next.t("© 2026 Clipsymphony. All Rights Reserved.")}
          </div>

        </div>
      </footer>
    </div>
  );
}
